import { ArrowUpRight, MapPin } from "lucide-react";
import { HERO_BG } from "@/lib/site";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden"
    >
      <img
        src={HERO_BG}
        alt="Modern home framing construction"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/75 via-navy-deep/55 to-navy-deep/85" />

      {/* Content (top) */}
      <div className="relative pt-28 lg:pt-32 pb-[180px] sm:pb-[200px] px-6 md:px-12 lg:px-16">
        <div className="flex items-center gap-2 text-white/80 fp-rise">
          <MapPin className="w-4 h-4 text-sunrise shrink-0" />
          <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em]">
            Utah · Residential &amp; Commercial
          </span>
        </div>

        <h1 className="mt-6 sm:mt-8 max-w-5xl font-display font-black tracking-tight text-white leading-[0.95] text-[40px] sm:text-6xl lg:text-8xl fp-rise">
          Building Utah, <br />
          <span className="text-sunrise">peak to peak.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-white/85 text-base sm:text-lg leading-relaxed fp-rise-2">
          Five Peaks Construction is a full-service builder serving Utah homeowners, businesses, and
          property managers. From new framing to final paint — we own every step.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 fp-rise-3">
          <a
            href="#contact"
            data-testid="hero-cta-quote"
            className="group bg-sunrise hover:bg-sunrise-hover text-white px-6 sm:px-8 py-4 sm:py-5 text-[13px] sm:text-sm font-bold tracking-wider uppercase inline-flex items-center justify-center gap-3 transition-colors rounded-xl shadow-lg shadow-sunrise/20"
          >
            Request a Free Estimate
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href="#portfolio"
            data-testid="hero-cta-work"
            className="border-2 border-white text-white px-6 sm:px-8 py-4 sm:py-5 text-[13px] sm:text-sm font-bold tracking-wider uppercase hover:bg-white hover:text-navy transition-colors rounded-xl text-center"
          >
            See Our Work
          </a>
        </div>
      </div>

      {/* Stat strip — pinned to bottom; content above always has matching pb- to avoid overlap */}
      <div className="absolute bottom-0 inset-x-0">
        <div className="px-4 sm:px-6 md:px-12 lg:px-16 pb-6 sm:pb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/20 md:divide-x md:divide-white/20">
            <Stat n="200+" l="Projects Delivered" />
            <Stat n="9" l="Trades In-House" />
            <Stat n="15yr" l="Combined Experience" />
            <Stat n="100%" l="Licensed & Insured" />
          </div>
        </div>
      </div>

      {/* Decorative vertical marquee (desktop xl only) */}
      <div className="absolute top-24 right-0 hidden xl:block pointer-events-none">
        <div className="rotate-90 origin-top-right translate-x-full text-white/40 text-xs tracking-[0.4em] uppercase">
          Five Peaks Construction LLC · Est. Utah
        </div>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div className="px-3 sm:px-4 md:px-6 py-4 sm:py-6">
      <div className="num-label text-white text-2xl sm:text-3xl md:text-5xl leading-none">{n}</div>
      <div className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.2em] mt-2">{l}</div>
    </div>
  );
}
